<template>
  <article
    class="waajo-kanban-card"
    :class="[
      'is-' + statusClass,
      { 'is-busy': busy, 'is-menu-open': showMenu }
    ]"
    :data-ticket-id="ticket.id"
  >
    <!-- Seluruh area utama kartu adalah drag handle. -->
    <div
      class="ticket-card-drag-area"
      :class="{ 'is-drag-disabled': dragDisabled }"
    >
      <div class="waajo-kanban-card-top">
        <span class="waajo-kanban-card-grip" aria-hidden="true">
          <i class="fa fa-ellipsis-v"></i>
          <i class="fa fa-ellipsis-v"></i>
        </span>
        <span class="waajo-kanban-card-number">{{ ticket.ticket_number }}</span>
        <span v-if="attachmentCount > 0" class="waajo-kanban-card-attach" :title="attachmentCount + ' lampiran gambar'">
          <i class="fa fa-paperclip"></i> {{ attachmentCount }}
        </span>
        <span v-if="busy" class="waajo-kanban-card-busy" title="Menyimpan...">
          <i class="fa fa-spinner fa-spin"></i>
        </span>
      </div>

      <h4 class="waajo-kanban-card-title">{{ ticket.title }}</h4>

      <div class="waajo-kanban-card-customer">
        <i class="fa fa-user"></i>
        <span>{{ ticket.contact_name || 'Tanpa nama' }}</span>
      </div>
      <div class="waajo-kanban-card-phone">
        <i class="fa fa-whatsapp"></i>
        <span>{{ formatTicketPhone(ticket.contact_number) }}</span>
      </div>

      <div class="waajo-kanban-card-chips">
        <span class="waajo-kanban-chip">{{ categoryLabel(ticket.category) }}</span>
        <span class="waajo-kanban-chip is-priority" :class="'is-' + ticket.priority">{{ priorityLabel(ticket.priority) }}</span>
        <span v-if="ticket.follow_up_datetime" class="waajo-kanban-chip is-followup" :title="'Jadwal tindak lanjut: ' + formatTicketDateTime(ticket.follow_up_datetime)">
          <i class="fa fa-calendar"></i> {{ formatKanbanDate(ticket.follow_up_datetime) }}
        </span>
      </div>
    </div>

    <!-- Footer bukan drag handle: klik membuka detail. -->
    <div
      class="ticket-card-footer"
      role="button"
      tabindex="0"
      title="Buka detail tiket"
      @click="openDetail"
      @keydown.enter.prevent="openDetail"
      @keydown.space.prevent="openDetail"
    >
      <span class="waajo-kanban-card-pic">
        <span class="waajo-kanban-avatar">{{ picInitial }}</span>
        <span class="waajo-kanban-pic-name">{{ ticket.assigned_agent_name || 'Belum ada PIC' }}</span>
      </span>

      <div class="waajo-kanban-card-menu-wrap" @click.stop @keydown.stop>
        <button
          type="button"
          class="waajo-kanban-card-menu-btn"
          :disabled="busy"
          title="Tindakan tiket"
          @click.stop="toggleMenu"
        >
          <i class="fa fa-ellipsis-h"></i>
        </button>

        <div v-if="showMenu" class="waajo-kanban-card-menu" @click.stop>
          <button type="button" class="waajo-kanban-card-menu-item" @click.stop="openDetail">
            <i class="fa fa-external-link"></i> Lihat Detail
          </button>
          <div class="waajo-kanban-card-menu-label">Pindahkan ke</div>
          <button
            v-for="item in otherStatuses"
            :key="item.value"
            type="button"
            class="waajo-kanban-card-menu-item"
            :disabled="busy"
            @click.stop="moveTo(item.value)"
          >
            <i :class="item.icon"></i> {{ item.label }}
          </button>
        </div>
      </div>

      <span class="waajo-kanban-card-chevron"><i class="fa fa-angle-right"></i></span>
    </div>
  </article>
</template>

<script>
import {
  TICKET_STATUSES,
  ticketCategoryLabel,
  ticketPriorityLabel,
  formatTicketPhone,
  formatTicketDateTime
} from '@/utils/ticket'

const STATUS_ICONS = {
  open: 'fa fa-envelope-open-o',
  in_progress: 'fa fa-spinner',
  resolved: 'fa fa-check-circle'
}

export default {
  name: 'TicketKanbanCard',
  props: {
    ticket: {
      type: Object,
      required: true
    },
    busy: {
      type: Boolean,
      default: false
    },
    dragDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showMenu: false
    }
  },
  computed: {
    statusClass() {
      return String(this.ticket.status || 'open').replace(/_/g, '-')
    },
    attachmentCount() {
      return Number(this.ticket.attachment_count) || 0
    },
    picInitial() {
      const name = String(this.ticket.assigned_agent_name || '').trim()
      if (!name) return '?'
      return name.charAt(0).toUpperCase()
    },
    otherStatuses() {
      return TICKET_STATUSES
        .filter(item => item.value !== this.ticket.status)
        .map(item => Object.assign({}, item, { icon: STATUS_ICONS[item.value] || 'fa fa-circle' }))
    }
  },
  watch: {
    busy(value) {
      if (value) this.showMenu = false
    }
  },
  beforeDestroy() {
    this.detachOutsideListener()
  },
  methods: {
    categoryLabel: ticketCategoryLabel,
    priorityLabel: ticketPriorityLabel,
    formatTicketPhone,
    formatTicketDateTime,
    formatKanbanDate(unix) {
      const value = formatTicketDateTime(unix, '')
      return value ? value.replace(/\s\d{2}:\d{2}$/, '') : ''
    },
    toggleMenu() {
      if (this.dragDisabled || this.busy) return
      this.showMenu = !this.showMenu
      if (this.showMenu) {
        this.$nextTick(() => {
          document.addEventListener('click', this.handleOutsideClick)
        })
      } else {
        this.detachOutsideListener()
      }
    },
    handleOutsideClick() {
      this.showMenu = false
      this.detachOutsideListener()
    },
    detachOutsideListener() {
      if (typeof document === 'undefined') return
      document.removeEventListener('click', this.handleOutsideClick)
    },
    openDetail() {
      this.showMenu = false
      this.detachOutsideListener()
      this.$emit('open', this.ticket)
    },
    moveTo(status) {
      this.showMenu = false
      this.detachOutsideListener()
      if (!status || status === this.ticket.status) return
      this.$emit('move', { ticket: this.ticket, status })
    }
  }
}
</script>

<style scoped>
.waajo-kanban-card {
  position: relative;
  padding: 13px 14px 11px;
  border: 1px solid var(--waajo-border);
  border-left: 3px solid transparent;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  cursor: default;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.waajo-kanban-card:hover {
  border-color: var(--waajo-brand-border-strong);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}
.waajo-kanban-card.is-open {
  border-left-color: var(--waajo-brand);
}
.waajo-kanban-card.is-in-progress {
  border-left-color: #3b82f6;
}
.waajo-kanban-card.is-resolved {
  border-left-color: #22c55e;
}
.waajo-kanban-card.is-busy {
  opacity: 0.7;
}
/* Seluruh area utama kartu adalah drag handle. */
.ticket-card-drag-area {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
}
.ticket-card-drag-area.is-drag-disabled,
.waajo-kanban-card.is-busy .ticket-card-drag-area {
  cursor: default;
}
.waajo-kanban-card-chosen .ticket-card-drag-area,
.waajo-kanban-card-drag .ticket-card-drag-area {
  cursor: grabbing;
}
.waajo-kanban-card-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 7px;
}
.waajo-kanban-card-grip {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  margin-right: 2px;
  color: #c0c9d6;
  cursor: inherit;
  font-size: 10px;
  line-height: 1;
  letter-spacing: -1px;
}
.waajo-kanban-card-grip i:last-child {
  margin-left: -3px;
}
.waajo-kanban-card-grip:hover {
  color: var(--waajo-brand);
}
.waajo-kanban-card-number {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.01em;
  color: var(--waajo-brand-ink);
}
.waajo-kanban-card-attach {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 9999px;
  background: var(--waajo-surface-soft);
  border: 1px solid var(--waajo-border);
  color: var(--waajo-text-muted);
  font-size: 10px;
  font-weight: 700;
}
.waajo-kanban-card-busy {
  margin-left: auto;
  color: var(--waajo-brand);
  font-size: 12px;
}
.waajo-kanban-card-title {
  margin: 0 0 9px;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--waajo-text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.waajo-kanban-card-customer,
.waajo-kanban-card-phone {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--waajo-text-body);
}
.waajo-kanban-card-customer i {
  color: var(--waajo-text-faint);
  font-size: 11px;
}
.waajo-kanban-card-phone {
  margin-top: 2px;
  color: #128c7e;
  font-weight: 600;
}
.waajo-kanban-card-phone i {
  font-size: 12px;
}
.waajo-kanban-card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 9px 0 10px;
}
.waajo-kanban-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 7px;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  background: #f8fafc;
  color: var(--waajo-text-muted);
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.waajo-kanban-chip.is-priority.is-urgent {
  border-color: var(--waajo-priority-urgent-border);
  background: var(--waajo-priority-urgent-bg);
  color: var(--waajo-priority-urgent-ink);
}
.waajo-kanban-chip.is-priority.is-normal {
  border-color: var(--waajo-priority-normal-border);
  background: var(--waajo-priority-normal-bg);
  color: var(--waajo-priority-normal-ink);
}
.waajo-kanban-chip.is-priority.is-low {
  border-color: var(--waajo-priority-low-border);
  background: var(--waajo-priority-low-bg);
  color: var(--waajo-priority-low-ink);
}
.waajo-kanban-chip.is-followup {
  border-color: var(--waajo-brand-border);
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink);
}
/* Footer bukan drag handle; klik membuka detail. */
.ticket-card-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #f4f7fb;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  border-radius: 0 0 8px 8px;
  outline: none;
}
.ticket-card-footer:hover .waajo-kanban-pic-name,
.ticket-card-footer:focus .waajo-kanban-pic-name {
  color: var(--waajo-brand-ink);
}
.ticket-card-footer:hover .waajo-kanban-card-chevron,
.ticket-card-footer:focus .waajo-kanban-card-chevron {
  color: var(--waajo-brand);
  transform: translateX(2px);
}
.ticket-card-footer:focus-visible {
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-kanban-card-chevron {
  transition: color 0.15s ease, transform 0.15s ease;
}
.waajo-kanban-card-pic {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
}
.waajo-kanban-avatar {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--waajo-brand-soft);
  border: 1px solid var(--waajo-brand-border);
  color: var(--waajo-brand-ink);
  font-size: 10px;
  font-weight: 800;
}
.waajo-kanban-pic-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--waajo-text-body);
}
.waajo-kanban-card-menu-wrap {
  position: relative;
  flex: 0 0 auto;
}
.waajo-kanban-card-menu-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  color: var(--waajo-text-muted);
  font-size: 12px;
}
.waajo-kanban-card-menu-btn:hover,
.waajo-kanban-card-menu-btn:focus {
  border-color: var(--waajo-border);
  background: var(--waajo-surface-soft);
  color: var(--waajo-text);
}
.waajo-kanban-card-menu {
  position: absolute;
  right: 0;
  bottom: 28px;
  z-index: 20;
  width: 182px;
  padding: 5px;
  border: 1px solid var(--waajo-border);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
}
.waajo-kanban-card-menu-item {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 7px 9px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--waajo-text-body);
  font-size: 12px;
  font-weight: 600;
  text-align: left;
}
.waajo-kanban-card-menu-item i {
  width: 14px;
  color: var(--waajo-brand-ink);
  font-size: 11px;
  text-align: center;
}
.waajo-kanban-card-menu-item:hover,
.waajo-kanban-card-menu-item:focus {
  background: var(--waajo-brand-soft-2);
}
.waajo-kanban-card-menu-item:disabled {
  opacity: 0.5;
}
.waajo-kanban-card-menu-label {
  padding: 6px 9px 3px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--waajo-text-faint);
}
.waajo-kanban-card-chevron {
  color: #cbd5e1;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketKanbanCard.vue